import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { ResourceLanguage } from 'i18next';
import en from './locales/en.json';
import fr from './locales/fr.json';
import rw from './locales/rw.json';
import sw from './locales/sw.json';
import { translateResourceBundle, translateText } from '../services/translationService';

export const staticLanguageOptions = [
  { code: 'en', labelKey: 'language.english', shortLabel: 'EN' },
  { code: 'sw', labelKey: 'language.swahili', shortLabel: 'KISW' },
  { code: 'fr', labelKey: 'language.french', shortLabel: 'FR' },
  { code: 'rw', labelKey: 'language.kinyarwanda', shortLabel: 'RW' },
] as const;

const resources = {
  en: { translation: en },
  fr: { translation: fr },
  sw: { translation: sw },
  rw: { translation: rw },
};

const preferredLanguage =
  typeof window === 'undefined' ? 'en' : window.localStorage.getItem('haapa.language') || 'en';

const missingTranslationRequests = new Set<string>();

i18n.use(initReactI18next).init({
  resources,
  lng: preferredLanguage,
  supportedLngs: ['en', 'fr', 'sw', 'rw'],
  nonExplicitSupportedLngs: true,
  fallbackLng: 'en',
  saveMissing: true,
  missingKeyHandler: (lngs, _ns, key, fallbackValue) => {
    const value = typeof fallbackValue === 'string' ? fallbackValue : '';
    if (!value.trim()) return;

    // Always keep an English source string in memory so other languages can be generated on demand.
    if (!i18n.exists(key, { lng: 'en' })) {
      i18n.addResource('en', 'translation', key, value, { silent: true });
    }

    const list = Array.isArray(lngs) ? lngs : [lngs];
    list
      .map((lng) => (lng || '').toString().toLowerCase())
      .filter(Boolean)
      .filter((lng) => lng !== 'en')
      .forEach((lng) => {
        if (i18n.exists(key, { lng })) return;
        const requestKey = `${lng}:${key}`;
        if (missingTranslationRequests.has(requestKey)) return;
        missingTranslationRequests.add(requestKey);
        translateText(value, lng, 'en')
          .then((translated) => {
            i18n.addResource(lng, 'translation', key, translated || value, { silent: true });
            // Ensure UI updates once the translation arrives.
            i18n.emit('added', lng, 'translation', key);
          })
          .finally(() => {
            missingTranslationRequests.delete(requestKey);
          });
      });
  },
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

i18n.on('languageChanged', (language) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('haapa.language', language);
  }
});

export const ensureLanguageLoaded = async (languageCode: string): Promise<void> => {
  const normalizedLanguageCode = languageCode.trim().toLowerCase();
  if (!normalizedLanguageCode) {
    return;
  }

  if (i18n.hasResourceBundle(normalizedLanguageCode, 'translation')) {
    return;
  }

  const sourceBundle = en as ResourceLanguage;
  const translatedBundle = await translateResourceBundle(sourceBundle, normalizedLanguageCode);
  i18n.addResourceBundle(normalizedLanguageCode, 'translation', translatedBundle, true, true);
};

export default i18n;
